import type {
  ConditionGroupOperator,
  RuleCondition,
} from "@redotech/redo-flows-common/rules/schemas";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback } from "react";
import { ConditionItem } from "./condition-item";

export interface ConditionListProps {
  conditions: RuleCondition[];
  onChange: (conditions: RuleCondition[]) => void;
  operator: ConditionGroupOperator;
}

export const ConditionList = memo(function ConditionList({
  conditions,
  onChange,
  operator,
}: ConditionListProps) {
  const { t } = useTranslation();
  const handleConditionChange = useCallback(
    (index: number, newCondition: RuleCondition) => {
      const newConditions = [...conditions];
      newConditions[index] = newCondition;
      onChange(newConditions);
    },
    [conditions, onChange],
  );

  const handleConditionDelete = useCallback(
    (index: number) => {
      onChange(conditions.filter((_, i) => i !== index));
    },
    [conditions, onChange],
  );

  const badgeColor = operator === "AND" ? "blue" : "warning";
  const badgeText = operator === "AND" ? t("And") : t("Or");

  return (
    <Flex dir="column" gap="none">
      {conditions.map((condition, index) => (
        <Flex dir="column" gap="none" key={index}>
          {index > 0 && (
            <Flex align="flex-start" dir="column" gap="none">
              <Flex align="center" dir="column" gap="sm">
                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: "var(--redo-colors-gray-modern-200)",
                  }}
                />
                <RedoBadge color={badgeColor} size="sm" text={badgeText} />
                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: "var(--redo-colors-gray-modern-200)",
                  }}
                />
              </Flex>
            </Flex>
          )}

          <ConditionItem
            condition={condition}
            onChange={(newCondition) =>
              handleConditionChange(index, newCondition)
            }
            onDelete={() => handleConditionDelete(index)}
          />
        </Flex>
      ))}
    </Flex>
  );
});
